import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, Lock } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';

/**
 * The one place that tells a workshop where it stands: trial countdown while
 * it still has time, and a hard explanation once writing is switched off.
 * Hidden entirely for the founding account, which is never billed.
 */
export const SubscriptionBanner = () => {
  const { access, tenant, plans } = useTenant();
  if (!access || !tenant) return null;
  if (tenant.provider === 'internal') return null;

  const { state, days_left: daysLeft, reason, plan_name: planName } = access;

  if (state === 'trialing') {
    const urgent = daysLeft <= 3;
    // Workshops on monthly see what annual would keep in their pocket, while
    // the trial still gives them a reason to decide.
    const plan = (plans || []).find((p) => p.key === tenant.plan);
    // Net of any negotiated discount — see /api/tenant/me.
    const off = Number(tenant.discount_percent || 0);
    const net = (amount) => (off > 0
      ? Math.max(1, Math.round((Number(amount) || 0) * (100 - off) / 100))
      : Number(amount) || 0);
    const saving = tenant.billing_cycle !== 'yearly' && plan
      ? net(plan.price_monthly) * 12 - net(plan.price_yearly)
      : 0;
    return (
      <div
        className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm border-b ${
          urgent ? 'bg-amber-50 border-amber-200 text-amber-900'
                 : 'bg-slate-50 border-slate-200 text-slate-700'}`}
        data-testid="subscription-banner-trial"
      >
        <Clock size={15} className={urgent ? 'text-amber-600' : 'text-slate-400'} />
        <span>
          <b>{daysLeft === 0 ? 'Last day' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}</b>
          {' '}of your {planName} trial.
          {saving > 0 && (
            <span data-testid="subscription-banner-annual-hint">
              {' '}Paying yearly keeps <b>₹{saving.toLocaleString('en-IN')}</b>.
            </span>
          )}
        </span>
        <Link
          to="/admin/subscription"
          className="font-semibold underline underline-offset-2 hover:no-underline"
          data-testid="subscription-banner-cta"
        >
          Choose a plan
        </Link>
      </div>
    );
  }

  if (access.read_only) {
    return (
      <div
        className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm bg-red-50 border-b border-red-200 text-red-900"
        data-testid="subscription-banner-locked"
      >
        <Lock size={15} className="text-red-600" />
        <span>
          <b>{reason || 'This account is read-only.'}</b>{' '}
          You can still view and download everything — new entries are paused.
        </span>
        <Link
          to="/admin/subscription"
          className="font-semibold underline underline-offset-2 hover:no-underline"
          data-testid="subscription-banner-cta"
        >
          Reactivate
        </Link>
      </div>
    );
  }

  if (state === 'past_due') {
    return (
      <div className="flex items-center gap-3 px-4 py-2 text-sm bg-amber-50 border-b border-amber-200 text-amber-900"
           data-testid="subscription-banner-past-due">
        <AlertTriangle size={15} className="text-amber-600" />
        <span>We could not collect your last payment. Please update your payment method.</span>
        <Link to="/admin/subscription" className="font-semibold underline">Fix it</Link>
      </div>
    );
  }

  return null;
};

export default SubscriptionBanner;
